import { Directive, ElementRef, Input, OnInit, AfterViewInit, Renderer2 } from '@angular/core';

@Directive({
    // restrict to actual button elements to avoid accidentally attaching to non-button nodes
    selector: 'button[buttonBusy]',
    standalone: false
})
export class ButtonBusyDirective implements OnInit, AfterViewInit {
  @Input() set buttonBusy(isBusy: boolean) {
    this.refreshState(isBusy);
  }

  @Input() busyText: string;

  private button: HTMLElement | null = null;
  private originalButtonInnerHtml: string | null = null;
  private originalContentWrapper: HTMLElement | null = null;
  private spinnerElement: HTMLElement | null = null;

  constructor(private element: ElementRef, private renderer: Renderer2) {}

  public ngOnInit(): void {
    this.button = this.element?.nativeElement ?? null;
  }

  public ngAfterViewInit(): void {
    if (this.button) {
      // cache the original HTML so we can restore it later
      this.originalButtonInnerHtml = this.button.innerHTML;

      // wrap existing children in a wrapper so we can hide/show without destroying nodes
      const wrapper = this.renderer.createElement('span');
      this.renderer.addClass(wrapper, 'bb-original-content');

      // move child nodes into the wrapper
      while (this.button.firstChild) {
        this.renderer.appendChild(wrapper, this.button.firstChild);
      }

      // append wrapper to button
      this.renderer.appendChild(this.button, wrapper);
      this.originalContentWrapper = wrapper as HTMLElement;
    }
  }

  public refreshState(isBusy: boolean): void {
    if (!this.button) {
      return;
    }

    if (isBusy) {
      // disable button using renderer
      this.renderer.setAttribute(this.button, 'disabled', 'true');

      // create spinner element if not present
      if (!this.spinnerElement) {
        const spinner = this.renderer.createElement('span');
        this.renderer.addClass(spinner, 'bb-spinner');
        // add icon
        const icon = this.renderer.createElement('i');
        this.renderer.addClass(icon, 'fa');
        this.renderer.addClass(icon, 'fa-spin');
        this.renderer.addClass(icon, 'fa-spinner');
        this.renderer.appendChild(spinner, icon);
        // add text
        const spanText = this.renderer.createElement('span');
        const text = this.busyText ? this.busyText : 'Processing...';
        this.renderer.appendChild(spanText, this.renderer.createText(text));
        this.renderer.appendChild(spinner, spanText);

        this.spinnerElement = spinner as HTMLElement;
      }

      // hide original content wrapper (if present)
      if (this.originalContentWrapper) {
        this.renderer.setStyle(this.originalContentWrapper, 'display', 'none');
      }

      // append spinner
      if (this.spinnerElement && this.button) {
        this.renderer.appendChild(this.button, this.spinnerElement);
      }

      // mark that we disabled it (use data attribute)
      this.renderer.setAttribute(this.button, 'data-disabled-before', 'true');
    } else {
      // only restore if we previously disabled it
      const wasDisabled = this.button.getAttribute('data-disabled-before');
      if (!wasDisabled) {
        return;
      }

      // enable button using renderer
      this.renderer?.removeAttribute(this.button, 'disabled');
      // remove spinner if present
      if (this.spinnerElement && this.spinnerElement.parentNode) {
        this.renderer.removeChild(this.button, this.spinnerElement);
      }

      // show original content wrapper
      if (this.originalContentWrapper) {
        this.renderer.removeStyle(this.originalContentWrapper, 'display');
      }

      // remove the marker
      this.renderer?.removeAttribute(this.button, 'data-disabled-before');
    }
  }
}

